
import {Component, Vue} from 'vue-property-decorator';
import MenuTop from '@/components/MenuTop.vue';
import AppTopBar from '@/components/UI/AppTopBar.vue';
import AppStore from '@/components/AppStore';
import Http from '@/Http';
import {RnisEmulatorSubscription} from '@/components/rnis/emulator/RnisEmulatorSubscription';
import {RnisEmulatorLabels} from '@/components/rnis/emulator/RnisEmulatorLabels';
import {RnisEmulatorTask} from '@/components/rnis/emulator/RnisEmulatorTask';

@Component({
  components: {AppTopBar, MenuTop},
})
export default class RnisEmulatorView extends Vue {
  containerWidth = AppStore.containerWidth;

  loading = true;
  items: RnisEmulatorSubscription[] = [];
  tasksLoading = false;
  tasksDialog = false;
  selectedSubscription: RnisEmulatorSubscription | null = null;
  tasks: RnisEmulatorTask[] = [];
  regNumberFilter = '';
  statusFilter = '';

  readonly tableHeaders = [
    {text: 'Рег. номер', value: 'regNumber'},
    {text: 'Статус', value: 'status'},
    {text: 'Время действия', value: 'timeOfDay'},
    {text: 'Дата начала', value: 'startDate'},
    {text: 'Окончание', value: 'endDate'},
    {text: 'Осталось дней', value: 'daysLeft'},
    {text: 'Маршруты', value: 'actions', sortable: false}
  ];

  readonly tasksTableHeaders = [
    {text: 'Статус', value: 'status'},
    {text: 'Время действия', value: 'timeOfDay'},
    {text: 'Запуск', value: 'startedAt'},
    {text: 'Завершение', value: 'endedAt'},
    {text: 'Ошибка', value: 'errorMessage'}
  ];

  get regNumbers(): string[] {
    return AppStore.regNumbers;
  }

  get statusFilterItems(): { text: string; value: string }[] {
    const codes = ['CREATED', 'ACTIVE', 'SUSPENDED', 'EXPIRED', 'DEACTIVATED'] as const;
    return [
      {text: 'Все статусы', value: ''},
      ...codes.map((c) => ({
        text: RnisEmulatorLabels.subscriptionStatusLabel(c),
        value: c,
      })),
    ];
  }

  mounted() {
    AppStore.onContainerWidth((v) => (this.containerWidth = v));
    AppStore.setDisplayModeTable();
    this.load();
  }

  clearRegFilterAndLoad() {
    this.regNumberFilter = '';
    this.load();
  }

  clearStatusFilterAndLoad() {
    this.statusFilter = '';
    this.load();
  }

  load() {
    AppStore.showLoader();
    this.loading = true;
    const payload: Record<string, string> = {};
    const r = (this.regNumberFilter || '').trim();
    if (r) {
      payload.regNumber = r.toUpperCase();
    }
    const st = (this.statusFilter || '').trim();
    if (st) {
      payload.status = st;
    }
    Http.post<RnisEmulatorSubscription[]>('rnis/emulator/subscriptions', payload)
      .then((rows) => {
        this.items = Array.isArray(rows) ? rows : [];
      })
      .catch(() => {
        this.items = [];
      })
      .then(() => {
        this.loading = false;
        AppStore.hideLoader();
      });
  }

  formatDate(d: string | null | undefined): string {
    if (!d) {
      return '—';
    }
    const parts = d.split('-');
    if (parts.length !== 3) {
      return d;
    }
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }

  regNumberDisplay(reg: string): string {
    return (reg || '').toUpperCase();
  }

  statusLabel(status: string): string {
    return RnisEmulatorLabels.subscriptionStatusLabel(status);
  }

  statusColor(status: string): string {
    return RnisEmulatorLabels.subscriptionStatusColor(status);
  }

  timeOfDayLabel(t: string): string {
    return RnisEmulatorLabels.timeOfDayLabel(t);
  }

  timeOfDayIcon(t: string): string {
    return t === 'NIGHT' ? 'mdi-weather-night' : 'mdi-weather-sunny';
  }

  timeOfDayIconColor(t: string): string {
    return t === 'NIGHT' ? 'indigo darken-4' : 'yellow darken-4';
  }

  openTasksDialog(item: RnisEmulatorSubscription) {
    this.selectedSubscription = item;
    this.tasksDialog = true;
    this.loadTasks();
  }

  closeTasksDialog() {
    this.tasksDialog = false;
    this.selectedSubscription = null;
    this.tasks = [];
  }

  loadTasks() {
    const subscriptionId = this.selectedSubscription && this.selectedSubscription.subscriptionId;
    if (!subscriptionId) {
      this.tasks = [];
      return;
    }

    this.tasksLoading = true;
    Http.post<RnisEmulatorTask[]>('rnis/emulator/tasks', {subscriptionId})
      .then((rows) => {
        this.tasks = Array.isArray(rows) ? rows : [];
      })
      .catch(() => {
        this.tasks = [];
      })
      .then(() => {
        this.tasksLoading = false;
      });
  }

  taskStatusLabel(status: string): string {
    const map: Record<string, string> = {
      WAITING: 'Ожидает дня запуска',
      CALCULATING: 'Расчет маршрута',
      CALCULATED: 'Ожидание запуска',
      IN_WORK: 'Машина в пути',
      UNLOADING: 'Разгрузка',
      PARKING: 'Парковка',
      COMPLETED: 'Завершено',
      CANCELED: 'Отменено',
      ERROR: 'Ошибка',
    };
    return map[status] || status;
  }

  taskStatusColor(status: string): string {
    const map: Record<string, string> = {
      WAITING: 'grey',
      CALCULATING: 'orange',
      CALCULATED: 'green',
      IN_WORK: 'blue',
      UNLOADING: 'cyan',
      PARKING: 'light-blue',
      COMPLETED: 'success',
      CANCELED: 'error',
      ERROR: 'error',
    };
    return map[status] || 'grey';
  }

  formatUnixDateTime(ts: number | null | undefined): string {
    if (!ts) {
      return '—';
    }
    const d = new Date(ts * 1000);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  /** Парсинг Y-m-d без сдвига по часовому поясу (календарная дата). */
  private parseYmd(d: string): { y: number; m: number; day: number } | null {
    const parts = d.split('-');
    if (parts.length !== 3) {
      return null;
    }
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const day = Number(parts[2]);
    if (!y || !m || !day) {
      return null;
    }
    return {y, m, day};
  }

  /** Разница в календарных днях: end − start (0, если одна дата). */
  private calendarDaysBetween(startYmd: string, endYmd: string): number {
    const s = this.parseYmd(startYmd);
    const e = this.parseYmd(endYmd);
    if (!s || !e) {
      return 0;
    }
    const t1 = Date.UTC(s.y, s.m - 1, s.day);
    const t2 = Date.UTC(e.y, e.m - 1, e.day);
    return Math.round((t2 - t1) / 86400000);
  }

  private todayYmdLocal(): string {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const d = String(n.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /** Длина периода подписки в днях, границы включительно (01.01 … 01.08). */
  totalSubscriptionDays(startDate: string, endDate: string): number {
    const span = this.calendarDaysBetween(startDate, endDate);
    if (span < 0) {
      return 0;
    }
    return span + 1;
  }

  /**
   * Сколько календарных дней осталось внутри периода [startDate, endDate].
   * Если сегодня раньше начала — считаем от startDate; если после endDate — 0.
   */
  daysLeftInSubscription(startDate: string, endDate: string): number {
    const today = this.todayYmdLocal();
    if (today.localeCompare(endDate) > 0) {
      return 0;
    }
    const effectiveStart = today.localeCompare(startDate) < 0 ? startDate : today;
    if (effectiveStart.localeCompare(endDate) > 0) {
      return 0;
    }
    return this.calendarDaysBetween(effectiveStart, endDate) + 1;
  }

  /**
   * Доля оставшегося срока: 100% = полный круг = весь период ещё впереди.
   */
  daysProgress(startDate: string, endDate: string): number {
    const total = this.totalSubscriptionDays(startDate, endDate);
    if (total < 1) {
      return 0;
    }
    const left = this.daysLeftInSubscription(startDate, endDate);
    return Math.max(0, Math.min(100, Math.round((left / total) * 100)));
  }

  daysProgressColor(days: number): string {
    if (days <= 3) {
      return 'error';
    }
    if (days <= 10) {
      return 'warning';
    }
    return 'success';
  }

  progressColorByStatus(status: string, days: number): string {
    if (status === 'DEACTIVATED') {
      return 'grey darken-1';
    }
    if (status !== 'ACTIVE') {
      return 'grey';
    }
    return this.daysProgressColor(days);
  }

  progressTextByStatus(status: string, days: number): string {
    if (status === 'DEACTIVATED') {
      return '0';
    }
    if (status === 'SUSPENDED') {
      return String(days);
    }
    if (status !== 'ACTIVE') {
      return '—';
    }
    return String(days);
  }
}
